import { useApolloClient } from "@apollo/client/react";
import { useLayoutEffect, useRef } from "react";
import { RepeatIcon } from "lucide-react";

import {
  FinancialAccountFragment,
  FinancialAccountSyncStateEnum,
  StripeFinancialAccountFragment,
} from "@/__generated__/graphql";
import { NodeList } from "@/components/organisms/Node/NodeList";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations } from "@/constants";
import { useScope } from "@/hooks/useScope";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { Objects } from "@/objects";
import { State } from "@/state";

export const COMMAND_ID = "sync-account";

export type SyncAccountContextType = Record<string, never>;

type Row = FinancialAccountFragment | StripeFinancialAccountFragment;

/**
 * Returns true if a row is "syncable from this command": one Plaid feeds,
 * gated on ``plaidBankAccountId`` as the per-card "Sync" button is. The
 * picker hides accounts with no remote provider to pull from (manual
 * accounts, fed by statements through the per-row "Upload transactions"
 * action) and Stripe's, whose lines come from Stripe as they happen.
 */
const isSyncable = (row: Row): boolean =>
  row.__typename === "FinancialAccountObject" &&
  Boolean(row.plaidBankAccountId);

function Component() {
  const activeIndexRef = useRef<{
    data: Row | null;
    onClick: () => void;
  }>({ data: null, onClick: () => {} });
  const { activeEntityIdentifiers } = useScope();
  const apolloClient = useApolloClient();

  const handleSelect = async (row: Row) => {
    if (row.__typename === "FinancialAccountObject" && row.plaidBankAccountId) {
      // If the institution is awaiting reconnect (or stuck in
      // ``FAILED`` on a Plaid-linked account, which is the legacy
      // shape of the same condition), route straight to the
      // update-mode Link flow rather than firing a sync that we know
      // will just immediately re-fail.
      if (
        row.syncState === FinancialAccountSyncStateEnum.ReconnectRequired ||
        row.syncState === FinancialAccountSyncStateEnum.Failed
      ) {
        const institutionIdentifier = row.financialInstitution?.identifier;
        if (institutionIdentifier) {
          State.getState().pushToCommandStack({
            commandDefinitionId: "reconnect-bank-account",
            context: {
              financialInstitutionIdentifier: institutionIdentifier,
              institutionName:
                row.financialInstitution?.institutionName ?? null,
            },
          });
        }
        return;
      }

      // Fire-and-close: ``sync_financial_account`` flips state to
      // ``SYNCING`` synchronously and publishes the Plaid topic; the
      // card-level chip on ``FinancialAccountsView`` reflects progress
      // so we don't need to keep this dialog open.
      try {
        await apolloClient.mutate({
          mutation: Mutations.SYNC_FINANCIAL_ACCOUNT,
          variables: {
            input: { financialAccountIdentifier: row.identifier },
          },
        });
      } catch (error) {
        State.getState().setSnackbarMessage(
          `Couldn't start sync: ${(error as Error)?.message ?? "unknown error"}.`,
        );
      }
      State.getState().clearCommandStack();
    }
  };

  return (
    <StickyLayout
      stickyBottomChildren={
        <CommandCenterListFooter
          buttons={[
            {
              variant: "default",
              size: "sm",
              children: "Sync",

              onClick: () => {
                activeIndexRef.current.onClick?.();
              },

              hotkey: "enter",
            },
          ]}
        />
      }
    >
      <NodeList
        {...Objects.FinancialAccountObject.getReferenceValueOptions(
          activeEntityIdentifiers,
        )}
        onSelectOption={(row: Row) => {
          handleSelect(row);
        }}
        filterOptions={(options: Row[]) => options.filter(isSyncable)}
        onActiveIndexChange={(data: Row, onClick) => {
          activeIndexRef.current.data = data;
          activeIndexRef.current.onClick = onClick;
        }}
        className="max-w-none"
      />
    </StickyLayout>
  );
}

export function SyncAccountCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Sync account",
      description: "Pick an account to sync…",
      getLabel: () => "Sync account",
      component: Component,
      ReactIcon: RepeatIcon,
      group: "Integrations",
    });
    return () => {
      unregisterCommandDefinition(COMMAND_ID);
    };
  }, []);

  return null;
}
